import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Copy, Save, Loader2, Linkedin, Twitter, Youtube } from "lucide-react";
import { adminApi, streamSSE } from "@/lib/adminApi";

const PLATFORMS = [
  { id: "linkedin", label: "LinkedIn Post", icon: Linkedin },
  { id: "x", label: "X Thread", icon: Twitter },
  { id: "youtube", label: "YouTube Script", icon: Youtube },
];

export const RepurposePanel = ({ articleId, initial = {} }) => {
  const [tab, setTab] = useState("linkedin");
  const [texts, setTexts] = useState(() => Object.fromEntries(PLATFORMS.map((p) => [p.id, initial[p.id]?.text || ""])));
  const [busy, setBusy] = useState(null);

  const generate = async (platform) => {
    setBusy(platform);
    setTexts((t) => ({ ...t, [platform]: "" }));
    try {
      await streamSSE(`/admin/articles/${articleId}/repurpose/${platform}`, (d) => setTexts((t) => ({ ...t, [platform]: t[platform] + d })));
      toast.success("Content generated");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const generateAll = async () => { for (const p of PLATFORMS) { setTab(p.id); await generate(p.id); } };

  const save = async () => {
    try { await adminApi(`/admin/articles/${articleId}/repurpose/${tab}`, { method: "PUT", body: { text: texts[tab] } }); toast.success("Saved"); }
    catch (e) { toast.error(e.message); }
  };

  const copy = () => { navigator.clipboard.writeText(texts[tab]); toast.success("Copied to clipboard"); };
  const text = texts[tab];

  return (
    <div className="bg-[var(--bg-card)] border border-[var(--accent-violet)]/25 rounded-xl p-6" data-testid="repurpose-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-white font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-[var(--accent-violet-light)]" /> Repurpose This Content</h3>
          <p className="text-xs text-[var(--text-secondary)] mt-0.5">Generates platform-ready drafts in Raulvin's Voice Profile. Save the article first so the latest edits are used.</p>
        </div>
        <button onClick={generateAll} disabled={!!busy} className="flex items-center gap-1.5 bg-[var(--accent-violet)] hover:bg-[#6d28d9] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60" data-testid="repurpose-all-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Repurpose All
        </button>
      </div>
      <div className="flex gap-2 mb-3">
        {PLATFORMS.map((p) => (
          <button key={p.id} onClick={() => setTab(p.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${tab === p.id ? "bg-[var(--accent-violet)]/20 text-[var(--accent-violet-light)]" : "text-[var(--text-secondary)] hover:text-white"}`} data-testid={`repurpose-tab-${p.id}`}>
            <p.icon className="w-3.5 h-3.5" /> {p.label} {texts[p.id] && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
          </button>
        ))}
      </div>
      <textarea value={text} onChange={(e) => setTexts((t) => ({ ...t, [tab]: e.target.value }))} rows={12} placeholder={busy === tab ? "Writing..." : "Click Generate to create this draft."} className="w-full bg-[var(--bg-secondary)] border border-[rgba(124,58,237,0.15)] rounded-lg p-3 text-sm text-white leading-relaxed resize-y focus:outline-none focus:border-[var(--accent-violet)]" data-testid={`repurpose-text-${tab}`} />
      <div className="flex items-center justify-between mt-3">
        <span className="text-xs text-[var(--text-secondary)]" data-testid="repurpose-char-count">{text.length} characters</span>
        <div className="flex gap-2">
          <button onClick={() => generate(tab)} disabled={!!busy} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-[var(--accent-violet)]/15 text-[var(--accent-violet-light)] hover:bg-[var(--accent-violet)]/25 transition-colors disabled:opacity-60" data-testid="repurpose-generate-btn">
            {busy === tab ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {text ? "Regenerate" : "Generate"}
          </button>
          <button onClick={copy} disabled={!text} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-white border-glow hover:bg-white/5 transition-colors disabled:opacity-40" data-testid="repurpose-copy-btn"><Copy className="w-4 h-4" /> Copy</button>
          <button onClick={save} disabled={!text || !!busy} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-white border-glow hover:bg-white/5 transition-colors disabled:opacity-40" data-testid="repurpose-save-btn"><Save className="w-4 h-4" /> Save edits</button>
        </div>
      </div>
    </div>
  );
};
